<template>
  <ErrorBoundary>
    <div
      v-if="addOns && addOns?.length > 0"
      class="event-date-picker__input mt-2"
    >
      <label class="text-sm">{{ t('product.addOn.addOnLabel') }}</label>
      <MultiSelectDropdown
        v-model="selectedAddOns"
        :options="addOns"
        :placeholder="t('product.addOn.addOnPlaceholder')"
        :get-item-label="getAddOnLabel"
        :get-item-key="(addOn: EventAddOn) => addOn.id"
        :data-test-id="`event-buy-box--add-on-select`"
      />
    </div>
  </ErrorBoundary>
</template>

<script lang="ts" setup>
import type { EventAddOn, EventDescription } from '~/composables/types/event'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import { formatMoney } from '~/utils/currencyHelpers'
import { eventGetters } from '~/utils/eventGetters'
import { filterAddOnsByBookingType } from '~/utils/cartAddOnHelpers'
import { getEventAddOnPriceType } from '~/utils/eventAddOnGetters'
import MultiSelectDropdown from '~/components/common/MultiSelectDropdown.vue'

const eventData = inject<EventDescription>('eventData')!

const props = defineProps<{
  isBookingPrivate: boolean
}>()

const { locale, t } = useI18n()

const eventId = computed(() => eventGetters.getId(eventData))
const { data: addOnsResponse } = await useKftApiFetch<
  KftApiDefaultResponse<EventAddOn[]>
>(`v1/store/events/${eventId.value}/add-ons?include=media`)
const addOns = computed(() =>
  filterAddOnsByBookingType(
    addOnsResponse.value?.data || [],
    props.isBookingPrivate
  )
)

// Change from single addon to array of addons
const selectedAddOns = defineModel<EventAddOn[]>({
  default: () => [],
})

const getAddOnLabel = (addOn: EventAddOn) => {
  const price = getAddOnPrice(addOn)

  const priceSuffix =
    getEventAddOnPriceType(addOn) === 'PER_SESSION'
      ? t('product.ticketCardsSection.perGroup')
      : t('product.ticketCardsSection.perPerson')
  return price ? `${addOn?.name} : ${price} ${priceSuffix}` : addOn?.name
}

const getAddOnPrice = (addOn: EventAddOn) => {
  if (!addOn?.unit_price?.amount) {
    return ''
  }

  const amount =
    typeof addOn?.unit_price?.amount === 'number'
      ? addOn?.unit_price?.amount / 100
      : parseInt(addOn?.unit_price?.amount, 10) / 100

  return formatMoney(amount, locale.value, 'EUR', 2)
}
</script>
